/**
 * `/admin/registrations` and `/admin/members` - spec 03's queue and member
 * surfaces (FR-8.3, FR-8.4).
 *
 * Note the path the app links to: `/admin/members/{id}`. Spec 05's Contracts
 * name `/admin/users/:id`, which was never built - BACKLOG B-18 asks spec 12 to
 * link to the path that exists, which is what `memberPath` below does.
 */

import { request } from "./client";
import type { MemberStatus, Page } from "./types";

export type StatusEvent = {
  id: string;
  from_status: string | null;
  to_status: string | null;
  remarks: string | null;
  actor_admin_id: string | null;
  created_at: string;
};

export type RegistrationQueueItem = {
  id: string;
  full_name: string;
  mobile_number: string;
  email: string | null;
  status: MemberStatus;
  created_at: string;
  /** The most recent lifecycle event, e.g. a prior `info_requested`. */
  last_event: StatusEvent | null;
};

export type MemberListItem = {
  id: string;
  full_name: string;
  mobile_number: string;
  email: string | null;
  status: MemberStatus;
  is_demo_account: boolean;
  created_at: string;
  approved_at: string | null;
  last_login_at: string | null;
};

export type MemberDetail = {
  id: string;
  role: string;
  status: MemberStatus;
  full_name: string;
  mobile_number: string;
  email: string | null;
  date_of_birth: string | null;
  gender: string | null;
  profile_image_url: string | null;
  is_demo_account: boolean;
  approved_at: string | null;
  approved_by: string | null;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  /** Spec 16 - captured at registration for the UNO team; read-only here. */
  referred_by: string | null;
  country: string | null;
  city: string | null;
  area_id: string | null;
  /** Spec 16 - set by the front desk once UNO approved the member's account. */
  uno_approved_at: string | null;
  uno_approved_by: string | null;
  /** The window the member's UNO card shows, as "YYYY-MM-DD". Both are required to set the flag. */
  uno_valid_from: string | null;
  uno_valid_to: string | null;
  status_events: StatusEvent[];
};

export type MemberUpdate = {
  full_name?: string;
  mobile_number?: string;
  email?: string | null;
  is_demo_account?: boolean;
  /** Spec 16 - sets or clears `uno_approved_at` / `uno_approved_by`. Setting it needs both dates. */
  uno_approved?: boolean;
  uno_valid_from?: string | null;
  uno_valid_to?: string | null;
  status?: MemberStatus;
  remarks?: string | null;
};

export type MemberListFilters = {
  q?: string;
  status?: MemberStatus;
  created_from?: string;
  created_to?: string;
  limit?: number;
  offset?: number;
};

/** Mirrors the server's `users_gender_check`; the create dialog's select. */
export const GENDERS = ["male", "female", "other", "prefer_not_to_say"] as const;
export type Gender = (typeof GENDERS)[number];

/**
 * `POST /admin/members` body (spec 17). Optional keys are OMITTED when the
 * admin left them blank - the server treats "" and absent the same, but absent
 * keeps the wire honest. The password is the one the front desk tells the
 * member; it is hashed on arrival and never comes back.
 */
export type MemberCreate = {
  full_name: string;
  mobile_number: string;
  password: string;
  email?: string;
  date_of_birth?: string;
  gender?: Gender;
  referred_by?: string;
  country?: string;
  city?: string;
  area_id?: string;
};

/** The built path, and the one the member detail route resolves. */
export function memberPath(userId: string): string {
  return `/members/${userId}`;
}

export function fetchRegistrationQueue(limit: number, offset: number): Promise<Page<RegistrationQueueItem>> {
  return request<Page<RegistrationQueueItem>>("/admin/registrations", {
    query: { status: "pending", limit, offset },
  });
}

export function fetchMembers(filters: MemberListFilters): Promise<Page<MemberListItem>> {
  return request<Page<MemberListItem>>("/admin/members", { query: { ...filters } });
}

export function fetchMember(userId: string): Promise<MemberDetail> {
  return request<MemberDetail>(`/admin/members/${userId}`);
}

/**
 * Approve, reject, or ask for information.
 *
 * All three go through the same queue action server-side, and all three need the
 * member to still be `pending` - a second decision answers 409
 * `registration_already_decided`. Only `request-info` requires remarks, because
 * FR-3.1.5 requires a reason the member can act on.
 */
/** `PATCH /admin/members/{id}` answers with the updated member plus a sentence. */
export type MemberOutcome = {
  member: MemberDetail;
  message: string;
};

/**
 * The three queue decisions answer with the MemberDetail ITSELF - no wrapper and
 * no `message` (`admin_accounts.py` sets `response_model=MemberDetail` on all
 * three). So a screen that wants to confirm the decision has to write its own
 * sentence; there is nothing served to render. Note the asymmetry with
 * `updateMember` below, which does return a `message`: the queue routes are the
 * thinner ones.
 */
export function approveRegistration(userId: string, remarks?: string): Promise<MemberDetail> {
  return request(`/admin/registrations/${userId}/approve`, {
    method: "POST",
    body: { remarks: remarks ?? null },
  });
}

export function rejectRegistration(userId: string, remarks?: string): Promise<MemberDetail> {
  return request(`/admin/registrations/${userId}/reject`, {
    method: "POST",
    body: { remarks: remarks ?? null },
  });
}

export function requestRegistrationInfo(userId: string, remarks: string): Promise<MemberDetail> {
  return request(`/admin/registrations/${userId}/request-info`, {
    method: "POST",
    body: { remarks },
  });
}

/**
 * `PATCH /admin/members/{id}` - profile fields and/or a status change in one
 * call. `role` is deliberately absent: FR-8.1b's "no self-service role
 * elevation" is enforced by the server refusing the field, and offering it here
 * would be a control that always errors.
 */
export function updateMember(userId: string, changes: MemberUpdate): Promise<MemberOutcome> {
  return request(`/admin/members/${userId}`, { method: "PATCH", body: changes });
}

/**
 * Spec 19 - `DELETE /admin/members/{id}`: delete a member's account on their
 * behalf. Irreversible; the reason is optional and kept on the status event.
 * 403 `demo_account_protected`; 409 `account_deleted` if already gone.
 */
export function deleteMember(userId: string, remarks: string | null): Promise<MemberOutcome> {
  return request(`/admin/members/${userId}`, { method: "DELETE", body: { remarks } });
}

/**
 * Spec 17 - `POST /admin/members`: the front desk creates an existing gym member
 * directly as `active`, skipping the request/approve queue. 409
 * `mobile_number_taken` / `email_taken`; 422 with `field` naming the bad input.
 */
export function createMember(payload: MemberCreate): Promise<MemberOutcome> {
  return request(`/admin/members`, { method: "POST", body: payload });
}
